import { useSuspenseQuery } from "@apollo/client/react";
import { ArrowUpRight, Plus, RotateCw } from "lucide-react";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import type { MutationRefetchQueries } from "~/common/types";
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui";
import { useAuth } from "~/features/authn/context";
import {
  GetBatchEvidencesJobDocument,
  type GetBatchEvidencesJobQuery,
  type GetBatchEvidencesJobQueryVariables,
  GetDataSourceEvidenceImportersDocument,
  type GetDataSourceEvidenceImportersQuery,
  type GetDataSourceEvidenceImportersQueryVariables,
} from "~/features/dataImportProjects/gqls";
import { useBatchEvidencesJobForm } from "~/features/dataImportProjects/hooks/useBatchEvidencesJobForm";
import { type BatchEvidencesJobFormData, BatchEvidencesJobType } from "~/features/dataImportProjects/types";
import { useTenant } from "~/features/tenant/context";
import { v2Route } from "~/v2/routes";
import { EvidenceImporterEditForm } from "./EvidenceImporterEditForm";

// Radix rejects an empty-string SelectItem value, so "unset" needs a stand-in.
const NONE = "__none";

interface BatchEvidencesJobFormProps {
  onSubmit: () => void;
  importProjectId: string;
  dataSourceId: string;
  batchEvidencesJobId: string | undefined;
  refetchQueriesOnEdit: MutationRefetchQueries;
  formId?: string;
}

export function BatchEvidencesJobForm({
  onSubmit,
  refetchQueriesOnEdit,
  batchEvidencesJobId,
  importProjectId,
  dataSourceId,
  formId,
}: BatchEvidencesJobFormProps) {
  const { t } = useTranslation();
  const [jobType, setJobType] = useState<BatchEvidencesJobType>(BatchEvidencesJobType.DbBasedScript);
  const [selectedImporterId, setSelectedImporterId] = useState<string | null>(null);
  const [addFormOpen, setAddFormOpen] = useState(false);
  const { tenantId } = useTenant();
  if (!tenantId) {
    throw new Error("Tenant ID is required");
  }

  const methods = useForm<BatchEvidencesJobFormData>();
  const { control, handleSubmit, register, setValue, reset } = methods;

  const { data } = useSuspenseQuery<GetBatchEvidencesJobQuery, GetBatchEvidencesJobQueryVariables>(
    GetBatchEvidencesJobDocument,
    {
      variables: { tenantId, id: batchEvidencesJobId ?? "" },
      skip: !batchEvidencesJobId,
    },
  );

  const { data: importersData, refetch } = useSuspenseQuery<
    GetDataSourceEvidenceImportersQuery,
    GetDataSourceEvidenceImportersQueryVariables
  >(GetDataSourceEvidenceImportersDocument, {
    variables: { tenantId, dataSourceId, dataImportProjectId: importProjectId },
  });

  useEffect(() => {
    if (!data) {
      return;
    }
    const job = data.batchEvidencesJobs[0];
    if (job) {
      setJobType((job.jobTypeId as BatchEvidencesJobType) ?? BatchEvidencesJobType.DbBasedScript);
      setSelectedImporterId(job.evidenceImporter?.id ?? null);
      reset({
        name: job.name ?? "",
        evidenceImporterId: job.evidenceImporter?.id ?? "",
      });
    }
  }, [data]);

  const { submit, processing } = useBatchEvidencesJobForm({
    importProjectId,
    dataSourceId,
    batchEvidencesJobId,
    refetchQueriesOnEdit,
    onSuccess: onSubmit,
  });

  const { user } = useAuth();
  if (!user) {
    throw Error("User does not exist!");
  }

  const job = batchEvidencesJobId ? data?.batchEvidencesJobs[0] : undefined;
  if (batchEvidencesJobId && !job) {
    throw Error("Job does not exist!");
  }

  const evidenceImporters = importersData?.evidenceImporters ?? [];

  return (
    <div className="min-w-[500px]">
      <form id={formId} onSubmit={handleSubmit(submit)} className="space-y-6 pt-2">
        {jobType === BatchEvidencesJobType.DbBasedScript && (
          <Controller
            name="evidenceImporterId"
            control={control}
            defaultValue={job?.evidenceImporter?.id ?? ""}
            render={({ field }) => (
              <div className="space-y-2">
                <Label>{t("model.evidenceImporter.modelName")}</Label>
                <div className="flex items-start gap-2">
                  <Select
                    value={evidenceImporters.find((importer) => importer.id === field.value)?.id ?? NONE}
                    onValueChange={(value) => {
                      const id = value === NONE ? "" : value;
                      field.onChange(id);
                      setSelectedImporterId(id || null);
                      // The job takes the importer's name by default.
                      // ref: https://www.notion.so/coten/job-name-301739d4418949f381758a328d220b39
                      const selected = evidenceImporters.find((importer) => importer.id === id);
                      if (selected) {
                        setValue("name", selected.name);
                      }
                    }}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>{t("ui.none")}</SelectItem>
                      {evidenceImporters.map((importer) => (
                        <SelectItem key={importer.id} value={importer.id}>
                          {importer.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => void refetch()}
                    aria-label={t("ui.reload")}
                  >
                    <RotateCw />
                  </Button>
                  {selectedImporterId && (
                    <Button type="button" variant="outline" size="sm" asChild>
                      <Link
                        to={v2Route.dataImportProjects.detail.$buildPath({
                          params: { id: importProjectId },
                          searchParams: { peekType: "evidenceImporter", peekId: selectedImporterId },
                        })}
                      >
                        <ArrowUpRight />
                        <span>{t("ui.view")}</span>
                      </Link>
                    </Button>
                  )}
                  <Button type="button" variant="outline" size="sm" onClick={() => setAddFormOpen(true)}>
                    <Plus />
                    <span>{t("ui.add")}</span>
                  </Button>
                </div>
              </div>
            )}
          />
        )}

        <div className="space-y-2">
          <Label htmlFor="batch-evidences-job-name">{t("model.common.name")}</Label>
          <Input id="batch-evidences-job-name" {...register("name")} defaultValue={job?.name ?? ""} />
        </div>

        {!formId && (
          <div className="flex justify-end">
            <Button type="submit" disabled={processing}>
              {t("ui.save")}
            </Button>
          </div>
        )}
      </form>

      <Dialog open={addFormOpen} onOpenChange={setAddFormOpen}>
        <DialogContent className="max-h-[90vh] max-w-6xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="capitalize">
              {t("ui.addX", { x: t("model.evidenceImporter.modelName") })}
            </DialogTitle>
          </DialogHeader>
          {addFormOpen && (
            <EvidenceImporterEditForm
              importProjectId={importProjectId}
              dataSourceId={dataSourceId}
              evidenceImporterId={null}
              onSubmit={() => {
                setAddFormOpen(false);
                void refetch();
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
